import { ErrorModal, getMomentTz, HelmetDexteel } from "@dexteel/mesf-core";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	type DialogProps,
	DialogTitle,
	TextField,
} from "@mui/material";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid2 from "@mui/material/Grid2";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import Alert from "@mui/lab/Alert";
import { type SyntheticEvent, useEffect, useMemo, useState } from "react";
import { deleteWorkOrder } from "../../../pages/work-orders/repositorie/WorkOrdersRepositorie";
import { useWorkOrderModalsContext } from "../context/work-order-modals.context";
import type { ModalWorkOrder } from "../models/ModalWorkOrder";
import type { WorkOrder } from "@/pages/work-orders/models/WorkOrder";
import { useGetAssetPath } from "@/shared/hooks/useGetAssetFullPath";
import { SimpleTextAreaControl } from "@/components/controls/SimpleSelectorControl";

const moment = getMomentTz();

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	data: ModalWorkOrder | null;
	suffixTitle?: string;
};

export function DeleteWorkOrderModal({
	show,
	onHide,
	data,
	suffixTitle,
}: Props) {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [deleteSuccess, setDeleteSuccess] = useState(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");

	const { state, actions } = useWorkOrderModalsContext();
	const {
		state: {
			modalWorkOrderSelected: {
				Comments,
				TemplateTitle,
				WorkOrderPlannedHours,
				WorkOrderNumberOfWorkers,
				WorkOrderOpenedBy,
				OpenTimestamp,
				WorkOrderName,
				AssetName,
				Title,
			},
		},
		actions: { setAssetName },
	} = useWorkOrderModalsContext();
	const [isLoadingAssetPath, setIsLoadingAssetPath] = useState<boolean>(true);
	const [errorAssetPath, setErrorAssetPath] = useState<string>("");
	const { GetAssetFullPath } = useGetAssetPath({
		setIsLoadingAssetPath,
		setErrorAssetPath,
		setAssetName,
	});
	//functions
	const handleCancel = () => {
		onHide(false);
	};

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setDeleteSuccess(false);
	};

	const formatDescription = (description: string): string => {
		if (description?.includes("/") && description.split("/").length > 3) {
			const parts = description.split("/");
			let formattedDescription = "";

			for (let i = 0; i < parts.length; i++) {
				if (i < parts.length - 2) {
					formattedDescription += ".../";
				} else if (i === parts.length - 2) {
					if (parts[i].length > 6) {
						formattedDescription += parts[i].substring(0, 6) + ".../";
					} else {
						formattedDescription += parts[i] + "/";
					}
				} else {
					formattedDescription += parts[i];
					if (i < parts.length - 1) {
						formattedDescription += "/";
					}
				}
			}

			return formattedDescription;
		} else {
			return description;
		}
	};

	const handleDeleteBtn = async () => {
		setIsSubmitLoading(true);
		const res = await deleteWorkOrder(
			state.modalWorkOrderSelected as WorkOrder,
		);
		if (res.ok) {
			setIsSubmitLoading(false);
			setOpenDeleteDialog(false);
			setTimeout(() => {
				setDeleteSuccess(true);
				onHide(true);
			}, 100);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	useEffect(() => {
		if (data != null && show) {
			actions.setComments(data?.Comments);
			actions.setTemplateTitle(data?.TemplateTitle);
			actions.setWorkOrderPlannedHours(data?.WorkOrderPlannedHours);
			actions.setWorkOrderNumberOfWorkers(data?.WorkOrderNumberOfWorkers);
			actions.setWorkOrderOpenedBy(data?.WorkOrderOpenedBy);
			actions.setOpenTimestamp(data?.OpenTimestamp);
			actions.setWorkOrderName(data?.WorkOrderName);
			GetAssetFullPath((data?.AssetId as number)?.toString());
			actions.setTitle(data?.Title);
			actions.setAssetName(data?.AssetName);
			actions.setWorkOrderId(data?.WorkOrderId);
		}
		setIsSubmitLoading(false);
	}, [show]);

	const description = useMemo(
		() => formatDescription(AssetName as string),
		[AssetName],
	);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Delete Work Order${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
			>
				<DialogTitle sx={{ padding: "15px 24px 10px 24px" }}>
					<Typography sx={{ fontSize: "1.3em", fontWeight: "bold", margin: 0 }}>
						DELETE: {Title}
					</Typography>
				</DialogTitle>
				<DialogContent dividers>
					<Grid2 container spacing={1.25}>
						<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 12, xs: 12 }}>
							<TextField
								label="Work order"
								variant="outlined"
								disabled
								value={WorkOrderName}
								fullWidth
								autoComplete="off"
							/>
						</Grid2>
						<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 12, xs: 12 }}>
							<TextField
								label="Asset"
								variant="outlined"
								disabled
								value={description || ""}
								fullWidth
								autoComplete="off"
							/>
						</Grid2>
						<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 6, xs: 6 }}>
							<TextField
								label="Opened by"
								variant="outlined"
								disabled
								value={WorkOrderOpenedBy}
								fullWidth
								autoComplete="off"
							/>
						</Grid2>
						<Grid2 sx={{ paddingRight: 0 }} size={{ md: 6, xs: 6 }}>
							<TextField
								label="Opened"
								variant="outlined"
								disabled
								value={moment(OpenTimestamp).format("MM/DD/YYYY hh:mm A")}
								fullWidth
								autoComplete="off"
							/>
						</Grid2>
						{TemplateTitle && (
							<>
								<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 12, xs: 12 }}>
									<TextField
										label="Original Title"
										variant="outlined"
										disabled
										value={TemplateTitle}
										fullWidth
										autoComplete="off"
									/>
								</Grid2>
								<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 6, xs: 6 }}>
									<TextField
										label="Planned Hours:"
										variant="outlined"
										disabled
										value={WorkOrderPlannedHours}
										fullWidth
										autoComplete="off"
									/>
								</Grid2>
								<Grid2 sx={{ paddingRight: 0 }} size={{ md: 6, xs: 6 }}>
									<TextField
										label="Number of Workers"
										variant="outlined"
										disabled
										value={WorkOrderNumberOfWorkers}
										fullWidth
										autoComplete="off"
									/>
								</Grid2>
							</>
						)}
						<Grid2
							sx={{ paddingLeft: 0, paddingRight: 0 }}
							size={{ md: 12, xs: 12 }}
						>
							<Card
								variant="outlined"
								sx={{
									backgroundColor: "#E9F2F8",
									marginTop: "0px",
									"& .input-group-text": {
										display: "none",
									},
								}}
							>
								<CardContent sx={{ padding: "12px !important" }}>
									<Typography
										sx={{
											fontSize: 15,
											fontWeight: "bold",
											color: "#0000008A",
										}}
									>
										Comments:
									</Typography>
									<SimpleTextAreaControl
										value={Comments || ""}
										disabled
										showColor={false}
										styleText={{
											height: "60px",
											border: "none",
											backgroundColor: "#E9F2F8",
											fontSize: 16,
										}}
									/>
								</CardContent>
							</Card>
						</Grid2>
					</Grid2>
				</DialogContent>
				<DialogActions
					sx={{ padding: "0px 24px", marginTop: "16px", paddingBottom: "16px" }}
				>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={handleCancel}
							>
								CANCEL
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
								disabled={isSubmitLoading}
								variant="contained"
								color="secondary"
								onClick={() => setOpenDeleteDialog(true)}
							>
								Delete
							</Button>
						</Grid2>
					</Grid2>
				</DialogActions>
			</Dialog>
			<Dialog
				open={openDeleteDialog}
				onClose={() => setOpenDeleteDialog(false)}
				aria-labelledby="responsive-dialog-title"
			>
				<DialogTitle id="responsive-dialog-title">WARNING</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ padding: 0, margin: 0 }}>
						Be careful, once deleted you will not be able to recover this work
						order, are you sure you want to delete it?
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ padding: "16px 24px" }}>
					<Button
						onClick={() => setOpenDeleteDialog(false)}
						autoFocus
						color="inherit"
					>
						CANCEL
					</Button>
					<Button
						autoFocus
						color="secondary"
						startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
						onClick={handleDeleteBtn}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
			<Snackbar
				open={deleteSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="error" onClose={handleClose}>
					Order deleted
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal error={errorAssetPath} onHide={() => setErrorAssetPath("")} />
		</>
	);
}
